import { CardContent, Tooltip } from "@mui/material";
import Svg from "components/svg/svg";
import {
  getLegendDisplayOpacity,
  shouldDisplayLegendRule
} from "helpers/legend-display";
import { LegendItem, Polygon as PolygonStyle, Raster } from "helpers/models";
import { Trans, useTranslation } from "react-i18next";
import { PolygonLayerStatistic, PolygonMetricValues } from "./types";
import styles from "./polygon-result.module.scss";

const EMPHASIZED_FATHOM_LAYER_IDS = new Set([
  "FL_Fluvial_Global_Fathom",
  "FL_Pluvial_Global_Fathom",
  "FL_Coastal_Global_Fathom"
]);

interface Props {
  item: PolygonLayerStatistic;
  legendInfo?: LegendItem;
  metrics?: PolygonMetricValues;
  onOpenFathom: () => void;
  formatNumber: (value?: number) => string;
  isExpanded: boolean;
}

const normalizeRiskLevelForLegend = (riskLevel: string, layerId: string) => {
  const normalizedRiskLevel = String(riskLevel || "").trim();

  if (layerId.includes("Fathom")) {
    if (
      ["5 years", "10 years", "20 years", "50 years"].includes(
        normalizedRiskLevel
      )
    ) {
      return "≤50 years";
    }

    if (normalizedRiskLevel === "1000 years") {
      return "≥1000 years";
    }
  }

  if (
    layerId === "FL_Fluvial_Official" &&
    normalizedRiskLevel === "> 500 years"
  ) {
    return "> 500 years (US)";
  }

  return normalizedRiskLevel;
};

function PolygonSummaryRow({
  item,
  legendInfo,
  metrics,
  onOpenFathom,
  formatNumber,
  isExpanded
}: Props) {
  const [t] = useTranslation();
  const valueLabel = metrics?.p90ValueLabel || "No Data";
  const intensity = Number(metrics?.p90 || 0);
  const layerId = String(legendInfo?.layerId || item.layerId || "");
  const normalizedValueLabel = normalizeRiskLevelForLegend(valueLabel, layerId);
  const shouldEmphasizeP90Classification =
    EMPHASIZED_FATHOM_LAYER_IDS.has(layerId);

  return (
    <CardContent className={styles.cardContentTop}>
      <div className={styles.cardHeader}>
        <div className={styles.headerLeft}>
          <div>{Svg({ icon: item.layerId })()}</div>
          <div className={styles.layerTitle}>
            {t("layerId." + item.layerId)}
          </div>
        </div>
        {item.layerId.includes("Fathom") && (
          <div
            onClick={onOpenFathom}
            className={styles.fathomBadge}
            role="button"
            tabIndex={0}
          >
            <div className={styles.fathomBadgeIcon}>star</div>
          </div>
        )}
      </div>

      {!isExpanded && (
        <div className={styles.summaryRow}>
          <div className={styles.classificationBlock}>
            <div className={styles.blockLabel}>
              <span>{t("drawShape.P90 Classification")}</span>
              <Tooltip
                title={<Trans i18nKey="drawShape.P90 ClassificationInfo" />}
              >
                <div className={styles.infoIcon}>info</div>
              </Tooltip>
            </div>
            <div className={styles.legendRow}>
              {valueLabel === "No Observation" ? (
                <div className={styles.legendNoObservationWrap}>
                  <div className={styles.legendNoObservationBar}></div>
                </div>
              ) : (
                (() => {
                  const isNoDataValue =
                    valueLabel === "Outside" ||
                    valueLabel === "No Data" ||
                    valueLabel === "No Observation" ||
                    valueLabel === undefined;

                  if (isNoDataValue) {
                    return <div className={styles.legendNoDataBar}></div>;
                  }

                  return (
                    <div className={styles.legendScaleOuter}>
                      <div className={styles.legendScaleInner}>
                        {(legendInfo?.rules || []).map(
                          (rule, index: number) => {
                            if (!rule.symbolizers?.[0]) {
                              return null;
                            }
                            if (
                              rule.title === "Outside" ||
                              rule.title === "No Data" ||
                              rule.title === "No Observation" ||
                              rule.title === undefined
                            ) {
                              return null;
                            }

                            const raster = (rule.symbolizers[0] as Raster)
                              ?.raster?.color;
                            const polygon = (
                              rule.symbolizers[0] as PolygonStyle
                            )?.polygon?.fill;
                            const rasterOpacity = (
                              rule.symbolizers[0] as Raster
                            )?.raster?.opacity;
                            const polygonOpacity = (
                              rule.symbolizers[0] as PolygonStyle
                            )?.polygon?.fillOpacity;

                            const color = raster || polygon;
                            const opacity = rasterOpacity ?? polygonOpacity;
                            const display = shouldDisplayLegendRule(
                              legendInfo?.layerId,
                              rule.title,
                              opacity
                            )
                              ? "flex"
                              : "none";

                            return (() => {
                              const normalizedRuleTitle =
                                normalizeRiskLevelForLegend(
                                  String(rule.title || ""),
                                  layerId
                                );
                              const isActiveLegendSegment =
                                String(rule.title || "") ===
                                  String(valueLabel || "") ||
                                normalizedRuleTitle === normalizedValueLabel;

                              return (
                                <div
                                  key={`${rule.title}-${index}`}
                                  style={{
                                    backgroundColor: color,
                                    opacity: getLegendDisplayOpacity(
                                      legendInfo?.layerId,
                                      rule.title,
                                      opacity
                                    ),
                                    display
                                  }}
                                  className={`${styles.legendSegment} ${isActiveLegendSegment ? styles.activeLegendSegment : ""}`}
                                ></div>
                              );
                            })();
                          }
                        )}
                      </div>
                    </div>
                  );
                })()
              )}
              <div
                className={`${styles.legendValueLabel} ${shouldEmphasizeP90Classification ? styles.legendValueLabelEmphasis : ""}`}
              >
                {t("legend." + valueLabel)}
              </div>
            </div>
          </div>

          {!item.layerId.includes("Fathom") && (
            <div className={styles.intensityBlock}>
              <div className={styles.blockLabel}>
                <span>{t("drawShape.P90 Intensity")}</span>
                <Tooltip
                  title={<Trans i18nKey="drawShape.P90 IntensityInfo" />}
                >
                  <div className={styles.infoIcon}>info</div>
                </Tooltip>
              </div>
              <div className={styles.intensityValue}>
                {intensity === 0 ? 1 : formatNumber(intensity)}
              </div>
            </div>
          )}
        </div>
      )}
    </CardContent>
  );
}

export default PolygonSummaryRow;
